import * as API from "@aws-amplify/api";
import { fetchAuthSession } from "@aws-amplify/auth";
const SIGN_IN_PATH = "/sign-in";
/**
 * Returns true if the error indicates expired/forbidden Cognito identity or auth (e.g. NotAuthorizedException).
 * Used to redirect to sign-in instead of showing a generic toast.
 */
export function isAuthError(error) {
    if (!error)
        return false;
    const err = error;
    const status = err?.response?.status ?? err?.response?.statusCode;
    if (err?.cause && isAuthError(err.cause))
        return true;
    if (err?.underlyingError && isAuthError(err.underlyingError))
        return true;
    const msg = typeof err?.message === "string" ? err.message : String(err?.message ?? "");
    const body = err?.body ?? err?.response?.body;
    const bodyStr = typeof body === "string" ? body : body ? JSON.stringify(body) : "";
    const combined = `${msg} ${bodyStr}`.toLowerCase();
    let parsedBody;
    try {
        parsedBody = typeof body === "string" ? JSON.parse(body) : body;
    }
    catch {
        parsedBody = undefined;
    }
    // A 403 from our own handlers (ForbiddenResponseHandler) is a valid,
    // authenticated request that was denied for app-level permission reasons
    // (e.g. role/org checks) — not an expired/invalid session. Its JSON body
    // always has a numeric `code`. An API Gateway authorizer "explicit deny"
    // 403 has no such shape, and IS a real auth failure that should redirect.
    const isAppLevelForbidden = status === 403 &&
        parsedBody &&
        typeof parsedBody === "object" &&
        typeof parsedBody.code === "number";
    if (isAppLevelForbidden) {
        return false;
    }
    if (combined.includes("notauthorizedexception") ||
        combined.includes("access to identity") ||
        combined.includes("is forbidden") ||
        combined.includes("unauthorized") ||
        status === 401 ||
        status === 403) {
        return true;
    }
    if (parsedBody &&
        typeof parsedBody === "object" &&
        parsedBody.__type === "NotAuthorizedException")
        return true;
    return false;
}
export function redirectToSignIn() {
    if (typeof window !== "undefined") {
        window.location.replace(SIGN_IN_PATH);
    }
}
/** Private API calls with Bearer token (Amplify DevApi). */
export async function apiHandler(method, url, data = {}) {
    try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();
        if (!accessToken) {
            redirectToSignIn();
            throw new Error("No access token");
        }
        const optionsData = {
            headers: { Authorization: `Bearer ${accessToken}` },
            body: data,
        };
        const apiMethod = API[method];
        if (!apiMethod)
            throw new Error(`Unsupported API method: ${method}`);
        const initCall = await apiMethod({
            apiName: "DevApi",
            path: url,
            options: optionsData,
        });
        const { body } = await initCall.response;
        const response = await body.json();
        return response;
    }
    catch (error) {
        if (isAuthError(error)) {
            redirectToSignIn();
            throw error;
        }
        console.error(error);
        throw error;
    }
}
/** Public API calls (no auth, Amplify DevApiPublic). */
export async function publicApiHandler(method, url, data = {}) {
    try {
        const inData = { body: data };
        const apiMethod = API[method];
        if (!apiMethod)
            throw new Error(`Unsupported API method: ${method}`);
        const publicApi = await apiMethod({
            apiName: "DevApiPublic",
            path: url,
            options: inData,
        });
        const { body } = await publicApi.response;
        const response = await body.json();
        return response;
    }
    catch (error) {
        console.error(error);
        throw error;
    }
}
